// A photo from the camera or the gallery, made fit to send from the dock: at most
// 2048 px on its long side, re-encoded as a JPEG at about 85% (migration 019).
//
// Why on the device and not the server: a phone photo is 3-12 MB and the dock's
// Wi-Fi is the reason the offline queue exists. Re-encoding also CONVERTS what the
// server will not keep - an iPhone's HEIC decodes in Safari and leaves here as a
// JPEG - and drops the EXIF block, GPS included, because a canvas carries pixels
// only. A photo the browser cannot decode is refused here with a sentence; the
// server checks the bytes again whatever arrives (api/src/domain/iirPhotos.ts).
import { MAX_PHOTO_BYTES } from './receiveModel';

export const MAX_EDGE_PX = 2048;
export const JPEG_QUALITY = 0.85;

/** The size to draw at: the long side at most `max`, the aspect kept, never enlarged. */
export function fitWithin(width: number, height: number, max = MAX_EDGE_PX): { width: number; height: number } {
  if (!(width > 0 && height > 0)) return { width: 0, height: 0 };
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export class PhotoUnreadable extends Error {}

async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      // The camera's orientation applied, so a portrait photo is not sent on its side.
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
    } catch {
      // fall through to <img>, which some Safari versions decode when createImageBitmap does not
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch {
    URL.revokeObjectURL(url);
    throw new PhotoUnreadable('This photo could not be opened on this device. Take it again with the camera button.');
  }
}

/** The file, scaled and re-encoded as a JPEG. Throws PhotoUnreadable with a sentence for the person. */
export async function scalePhoto(file: Blob): Promise<Blob> {
  const img = await decode(file);
  try {
    const { width, height } = fitWithin(img.width, img.height);
    if (width === 0) throw new PhotoUnreadable('This photo has no picture in it.');
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new PhotoUnreadable('This device could not prepare the photo.');
    ctx.drawImage(img.source, 0, 0, width, height);
    for (const quality of [JPEG_QUALITY, 0.7, 0.55]) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (!blob || blob.type !== 'image/jpeg') throw new PhotoUnreadable('This device could not make a JPEG of the photo.');
      if (blob.size <= MAX_PHOTO_BYTES) return blob;
    }
    throw new PhotoUnreadable('This photo is still over 8 MB after scaling. Take it again.');
  } finally {
    img.close();
  }
}
